import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, HelpCircle, FileText, Shield, ClipboardCheck, Car } from 'lucide-react';
import { WizardLayout } from '@/Components/shared/WizardLayout';
import type { WizardStepHandle } from '@/Components/shared/WizardLayout.types';
import { Step1ContractSelection } from '@/features/delivery-proof/components/Step1ContractSelection';
import type { ContractSelection } from '@/features/delivery-proof/components/ContractSelector';
import { Step2WarrantySelection } from '@/features/delivery-proof/components/Step2WarrantySelection';
import { Step3ElementsSelection } from '@/features/delivery-proof/components/Step3ElementsSelection';
import { Step4VehicleInfo } from '@/features/delivery-proof/components/Step4VehicleInfo';
import { useDeliveryProofForm } from '@/features/delivery-proof/hooks/useDeliveryProofForm';
import { useDeliveryProofStore } from '@/features/delivery-proof/stores/deliveryProofStore';
import { useDeliveryProof, deliveryProofKeys } from '@/features/delivery-proof/api/deliveryProofApi';
import { useAuth } from '@/hooks/auth/useAuth';
import { useToggleTutorials } from '@/hooks/useToggleTutorials';
import { useDeliveryProofTutorial } from '@/features/delivery-proof/tutorials/hooks/useDeliveryProofTutorial';
import { TutorialSpotlight } from '@/features/warranties/tutorials/components/TutorialSpotlight';
import { TutorialIntro } from '@/features/delivery-proof/tutorials/components/TutorialIntro';
import { getFilteredTutorialSteps } from '@/features/delivery-proof/tutorials/constants/deliveryProofTutorialSteps';
import { useCurrentUserWording } from '@/hooks/useVendorWording';
import { useSociety } from '@/features/societies/hooks/useSocieties';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useNotifications } from '@/routes/outil-pro/contexts/NotificationContext';
import { DeliveryProofPdfPreviewPanel, DP_PDF_PANEL_WIDTH } from './DeliveryProofPdfPreviewPanel';
import { useDpPdfPreviewStore } from '../stores/dpPdfPreviewStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useAvailableWarranties } from '@/features/delivery-proof/hooks/useAvailableWarranties';
import { resolveLogoUrl } from '@/features/societies/utils/logo.utils';
import { useContract } from '@/features/contracts/hooks/useContract';
import { useWizardBrowserBack } from '@/hooks/useWizardBrowserBack';

interface DeliveryProofNewPageProps {
  societyId?: string;
  onComplete?: () => void;
  onSaveDraft?: () => void;
  /** Mode embarqué dans un onglet (stepper/footer sticky au lieu de fixed) */
  embedded?: boolean;
}

/**
 * Page de création d'une nouvelle attestation de livraison
 * Workflow en 4 étapes avec stepper - Utilise WizardLayout partagé
 * Supporte la reprise de brouillon via ?dpId=xxx
 */
export const DeliveryProofNewPage = ({ societyId, onComplete, onSaveDraft, embedded }: DeliveryProofNewPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const dpId = searchParams.get('dpId');
  const wording = useCurrentUserWording();

  const STEPS = useMemo(() => [
    { number: 1, label: 'Sélection du contrat', icon: <FileText className="tw-w-6 tw-h-6" /> },
    { number: 2, label: wording.plural, icon: <Shield className="tw-w-6 tw-h-6" /> },
    { number: 3, label: 'Éléments remis', icon: <ClipboardCheck className="tw-w-6 tw-h-6" /> },
    { number: 4, label: 'Infos véhicule', icon: <Car className="tw-w-6 tw-h-6" /> },
  ], [wording]);

  const currentStep = useDeliveryProofStore((state) => state.currentStep);
  const deliveryProofId = useDeliveryProofStore((state) => state.deliveryProofId);
  const formData = useDeliveryProofStore((state) => state.formData);
  const {
    reset, loadDeliveryProof, goToStep, updateFormData, previousStep,
    submitAll, isCreating, isSubmittingAll, isSending,
  } = useDeliveryProofForm({ onComplete });
  const { showNotification } = useNotifications();
  const isSocietyLocked = !!societyId;

  // Auth (utilisé par le panel preview + tutoriel)
  const { user } = useAuth();
  const isExternalUser = useMemo(() => user?.type === 'EXTERNAL', [user?.type]);

  // PDF Preview panel — responsive
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === 'lg';
  const isPdfPreviewOpen = useDpPdfPreviewStore((s) => s.isOpen);
  // On mobile/tablet the panel is an overlay → no rightMargin shift
  // On desktop: 45% of viewport capped between 380 and 700px
  const panelRightMargin = useMemo(() => {
    if (!isDesktop || !isPdfPreviewOpen) return 0;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    return Math.min(DP_PDF_PANEL_WIDTH, Math.max(340, Math.round(vw * 0.38)));
  }, [isDesktop, isPdfPreviewOpen]);
  const dpSocietyId = formData.societyId || user?.societyId;
  const { data: dpSociety } = useSociety(dpSocietyId);

  // Société à afficher sur l'attestation : garant résolu via effectiveConfig
  // (cohérent avec le backend `resolveDisplaySociety`). Si guarantorId pointe
  // sur une autre société, on affiche celle-ci ; sinon fallback sur la société
  // émettrice.
  const { data: dpEffectiveConfig } = useEffectiveConfiguration(dpSocietyId);
  const dpGuarantorId = dpEffectiveConfig?.guarantorId || dpSocietyId;
  const { data: dpGuarantorSociety } = useSociety(
    dpGuarantorId && dpGuarantorId !== dpSocietyId ? dpGuarantorId : undefined,
  );
  const dpDisplaySociety = dpGuarantorSociety ?? dpSociety;
  const { data: availableWarranties } = useAvailableWarranties(deliveryProofId);

  // Sélection du contrat et dpNumber en Step 1 (remontés via callbacks)
  const [contractSelection, setContractSelection] = useState<ContractSelection | null>(null);
  const [previewDpNumber, setPreviewDpNumber] = useState('');

  // Détail complet du contrat sélectionné (beneficiary complet + vehicle complet)
  const selectedContractId = contractSelection?.type === 'contract' ? contractSelection.contract.id : undefined;
  const { data: selectedContractDetail } = useContract(selectedContractId);

  // Nettoyer le store Zustand quand on quitte le formulaire (unmount)
  // Pattern StrictMode-safe : setTimeout annulé au remount, exécuté au vrai unmount
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(resetTimer.current);
    return () => {
      resetTimer.current = setTimeout(() => reset(), 0);
    };
  }, [reset]);

  // Protection bouton retour navigateur (active uniquement à partir du step 2) :
  // - back en step 2+ → retour au step précédent, données préservées
  // - step 1 → comportement navigateur natif (l'utilisateur peut quitter librement)
  // - beforeunload (refresh / fermeture tab) → alerte navigateur native (step 2+)
  useWizardBrowserBack({
    currentStep,
    goToPreviousStep: previousStep,
    enabled: currentStep > 1,
    beforeUnloadEnabled: !isCreating && !isSubmittingAll && !isSending,
  });

  // Ref pour déléguer handleNext/handleSave aux steps
  const stepRef = useRef<WizardStepHandle>(null);
  const handleNext = useCallback(() => { stepRef.current?.handleNext(); }, []);

  // Sauvegarder l'attestation (appels API chaînés) puis rediriger
  const handleSave = useCallback(async () => {
    try {
      stepRef.current?.handleSave?.();
      await submitAll();
      showNotification('success', 'Attestation enregistrée');
      onSaveDraft?.();
    } catch {
      showNotification('error', 'Erreur lors de l\'enregistrement');
    }
  }, [submitAll, showNotification, onSaveDraft]);

  // Point 3 : Invalider le cache au montage pour forcer un fetch frais
  // (contractNum peut avoir été mis à jour en externe par linkDraftToContract)
  useEffect(() => {
    if (dpId) {
      queryClient.removeQueries({ queryKey: deliveryProofKeys.detail(dpId) });
    }
  }, [dpId, queryClient]);

  // Sync dpId dans l'URL après création en Step1 (survie au F5)
  useEffect(() => {
    if (deliveryProofId && !dpId) {
      setDraftLoaded(true); // Éviter que loadDeliveryProof écrase le state fraîchement créé
      setSearchParams((prev) => {
        prev.set('dpId', deliveryProofId);
        return prev;
      }, { replace: true });
    }
  }, [deliveryProofId, dpId, setSearchParams]);

  const { data: existingDp, isLoading: isLoadingDp } = useDeliveryProof(dpId || deliveryProofId || undefined);
  const [draftLoaded, setDraftLoaded] = useState(false);

  // Données preview dérivées : priorité DP existant > détail contrat complet > brouillon
  const previewBeneficiary = useMemo(() => {
    if (existingDp?.beneficiary) return existingDp.beneficiary;
    if (selectedContractDetail?.beneficiary) {
      const b = selectedContractDetail.beneficiary;
      return {
        firstName: b.firstName,
        lastName: b.lastName,
        email: b.email,
        address: b.address,
        postalCode: b.cp,
        city: b.city,
        phone: b.phoneNumber,
        societyName: b.society,
      };
    }
    if (contractSelection?.type === 'draft' && contractSelection.draft.formData?.beneficiary) {
      const b = contractSelection.draft.formData.beneficiary;
      return { firstName: b.firstName, lastName: b.lastName, email: b.email };
    }
    return undefined;
  }, [existingDp?.beneficiary, selectedContractDetail, contractSelection]);

  const previewVehicle = useMemo(() => {
    if (existingDp?.vehicle) return existingDp.vehicle;
    if (selectedContractDetail?.vehicle) {
      const v = selectedContractDetail.vehicle;
      return {
        registration: v.registration,
        brand: v.brand,
        model: v.model,
        eis: v.entryIntoService,
        kmStart: v.kmStart ? Number(v.kmStart) : undefined,
      };
    }
    if (contractSelection?.type === 'draft' && contractSelection.draft.formData?.vehicle) {
      const v = contractSelection.draft.formData.vehicle;
      return { registration: v.registration, brand: v.brand, model: v.model };
    }
    return undefined;
  }, [existingDp?.vehicle, selectedContractDetail, contractSelection]);

  const previewContractNum = useMemo(() => {
    if (formData.contractNum) return formData.contractNum;
    if (contractSelection?.type === 'contract') return contractSelection.contract.label;
    return undefined;
  }, [formData.contractNum, contractSelection]);

  // Initialisation : charger le brouillon existant OU reset
  useEffect(() => {
    if (dpId) {
      // Mode reprise DP existant : attendre les données fraîches
      if (existingDp && !draftLoaded) {
        loadDeliveryProof(existingDp);
        setDraftLoaded(true);
      }
    } else if (!useDeliveryProofStore.getState().deliveryProofId) {
      // Mode création : reset puis pré-remplir la société si fournie
      // Ne pas reset si createDeliveryProof vient de peupler le store (dpId pas encore dans l'URL)
      reset();
      if (societyId) {
        updateFormData({ societyId });
      }
    }
  }, [dpId, existingDp, draftLoaded, loadDeliveryProof, reset, societyId, updateFormData]);

  // Gérer les steps complétées pour la navigation
  const completedSteps = useMemo(() => {
    const steps = new Set<number>();
    for (let i = 1; i <= currentStep; i++) {
      steps.add(i);
    }
    return steps;
  }, [currentStep]);

  // Footer state par step
  const footerState = useMemo(() => {
    const base = {
      nextLabel: currentStep === 4 ? "Générer l'attestation" : 'Suivant',
    };

    if (currentStep === 1) {
      return { ...base, isSubmitting: isCreating };
    }
    if (currentStep === 4) {
      return { ...base, isSubmitting: isSubmittingAll || isSending };
    }
    // Steps 2-3 : navigation locale, pas de loading sur "Suivant"
    return { ...base, isSubmitting: false };
  }, [currentStep, isCreating, isSubmittingAll, isSending]);

  // ========== Tutoriel ==========
  const isTutorialEnabled = isExternalUser && user?.showTutorials !== false;
  const { enableTutorials, disableTutorials } = useToggleTutorials();
  // vendorType pour le wording dynamique des étapes du tutoriel (React Query cache la société déjà fetchée par useCurrentUserWording)
  const { data: userSociety } = useSociety(isExternalUser ? user?.societyId : undefined);

  const {
    state: tutorialState,
    startTutorial,
    nextStep: nextTutorialStep,
    previousStep: previousTutorialStep,
    skipTutorial,
    completeTutorial,
    restartAtStep,
    showIntro,
  } = useDeliveryProofTutorial();

  // Obtenir les étapes du tutoriel
  const tutorialSteps = useMemo(() => {
    return getFilteredTutorialSteps(userSociety?.vendorType);
  }, [userSociety?.vendorType]);

  // Afficher l'intro du tutoriel à chaque visite du wizard (si activé dans le profil)
  useEffect(() => {
    if (isTutorialEnabled && !tutorialState.hasBeenCompleted && !tutorialState.hasBeenSkipped && !tutorialState.showIntro && !tutorialState.isActive) {
      const timer = setTimeout(() => {
        showIntro();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isTutorialEnabled, tutorialState.hasBeenCompleted, tutorialState.hasBeenSkipped, tutorialState.showIntro, tutorialState.isActive, showIntro]);

  // Étape actuelle du tutoriel
  const currentTutorialStep = useMemo(() => {
    if (!tutorialState.isActive) return null;
    return tutorialSteps[tutorialState.currentStepIndex];
  }, [tutorialState.isActive, tutorialState.currentStepIndex, tutorialSteps]);

  // Vérifier si le champ actuel du tutoriel est valide
  const isTutorialFieldValid = useMemo(() => {
    if (!currentTutorialStep) return false;
    return currentTutorialStep.validation(formData);
  }, [currentTutorialStep, formData]);

  // Calculer l'index du premier step du wizardStep actuel (pour bouton Aide)
  const firstStepIndexForCurrentWizard = useMemo(() => {
    return tutorialSteps.findIndex(step => step.wizardStep === currentStep);
  }, [tutorialSteps, currentStep]);

  // Synchroniser le tutoriel quand le wizard step change (navigation via le footer)
  useEffect(() => {
    if (tutorialState.isActive) {
      const currentTutorialWizardStep = tutorialSteps[tutorialState.currentStepIndex]?.wizardStep;
      if (currentTutorialWizardStep !== undefined && currentTutorialWizardStep !== currentStep) {
        const firstIndex = tutorialSteps.findIndex(s => s.wizardStep === currentStep);
        if (firstIndex !== -1) {
          restartAtStep(firstIndex);
        }
      }
    }
  }, [currentStep, tutorialState.isActive, tutorialState.currentStepIndex, tutorialSteps, restartAtStep]);

  // Wrapper goToStep : synchronise le tutoriel quand l'utilisateur navigue manuellement
  const handleGoToStep = useCallback((step: number) => {
    goToStep(step);
    if (tutorialState.isActive) {
      const firstIndex = tutorialSteps.findIndex(s => s.wizardStep === step);
      if (firstIndex !== -1) {
        restartAtStep(firstIndex);
      }
    }
  }, [goToStep, tutorialState.isActive, tutorialSteps, restartAtStep]);

  // Gérer le passage à l'étape suivante du tutoriel
  const handleNextTutorialStep = useCallback(() => {
    if (tutorialState.currentStepIndex >= tutorialSteps.length - 1) {
      disableTutorials();
      completeTutorial();
      return;
    }

    const nextStep = tutorialSteps[tutorialState.currentStepIndex + 1];
    if (nextStep && nextStep.wizardStep !== currentStep) {
      // Soumettre le step actuel (crée le delivery proof si step 1, sauvegarde sinon)
      // puis le wizard avance automatiquement via nextStep() dans handleSubmit
      stepRef.current?.handleNext();
      return;
    }

    nextTutorialStep();
  }, [
    tutorialState.currentStepIndex,
    tutorialSteps,
    disableTutorials,
    completeTutorial,
    nextTutorialStep,
    currentStep,
  ]);

  // Gérer le retour à l'étape précédente du tutoriel
  const handlePreviousTutorialStep = useCallback(() => {
    if (tutorialState.currentStepIndex > 0) {
      const prevStep = tutorialSteps[tutorialState.currentStepIndex - 1];
      if (prevStep && prevStep.wizardStep !== currentStep) {
        handleGoToStep(prevStep.wizardStep);
        return;
      }
      previousTutorialStep();
    }
  }, [
    tutorialState.currentStepIndex,
    tutorialSteps,
    previousTutorialStep,
    currentStep,
    handleGoToStep,
  ]);

  // Loading pendant le chargement du brouillon ou du draft
  if (dpId && isLoadingDp) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
          <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-affinicar-primary" />
          <p className="tw-text-sm tw-text-gray-500">Chargement de l'attestation...</p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1ContractSelection ref={stepRef} isSocietyLocked={isSocietyLocked} onSelectionChange={setContractSelection} onDpNumberChange={setPreviewDpNumber} />;
      case 2:
        return <Step2WarrantySelection ref={stepRef} />;
      case 3:
        return <Step3ElementsSelection ref={stepRef} />;
      case 4:
        return <Step4VehicleInfo ref={stepRef} onComplete={onComplete} />;
      default:
        return null;
    }
  };

  return (
    <>
      <WizardLayout
        steps={STEPS}
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={handleGoToStep}
        allowNavigation={true}
        embedded={embedded}
        rightMargin={panelRightMargin}

        onPrevious={currentStep > 1 ? previousStep : undefined}
        onCancel={currentStep === 1 ? undefined : onComplete}
        onNext={handleNext}
        nextLabel={footerState.nextLabel}
        isSubmitting={footerState.isSubmitting}
        onSaveDraft={currentStep > 1 ? handleSave : undefined}
        isSavingDraft={isSubmittingAll}
        hideWhenTutorial
        isTutorialActive={tutorialState.isActive}
        helpAction={!tutorialState.isActive && !tutorialState.showIntro && isExternalUser && firstStepIndexForCurrentWizard !== -1 ? (
          <button
            type="button"
            onClick={() => { enableTutorials(); restartAtStep(firstStepIndexForCurrentWizard); }}
            className="tw-flex tw-items-center tw-gap-2 tw-px-3 sm:tw-px-5 tw-py-3 tw-bg-white tw-text-affinicar-primary tw-font-medium tw-rounded-xl tw-border tw-border-affinicar-lighter hover:tw-bg-affinicar-lighter tw-transition-colors"
            title="Relancer le tutoriel à partir de cette étape"
          >
            <HelpCircle className="tw-w-4 tw-h-4" />
            <span className="tw-hidden sm:tw-inline">Aide</span>
          </button>
        ) : undefined}
      >
        {renderStep()}
      </WizardLayout>

      {/* Panel de preview PDF HTML (toggle droit) */}
      <DeliveryProofPdfPreviewPanel
        formData={{ ...formData, contractNum: previewContractNum || formData.contractNum, dpNumber: previewDpNumber || formData.dpNumber }}
        beneficiary={previewBeneficiary}
        vehicle={previewVehicle}
        companyName={dpDisplaySociety?.name}
        companySiren={dpDisplaySociety?.siren || undefined}
        companyAddress={dpDisplaySociety?.address}
        companyCpCity={[dpDisplaySociety?.cp, dpDisplaySociety?.city].filter(Boolean).join(' ') || undefined}
        companyPhone={dpDisplaySociety?.phone}
        companyEmail={dpDisplaySociety?.email}
        vendorType={existingDp?.vehicle?.vendorType === 2 ? 'Intermédiaire' : 'Vendeur'}
        logoUrl={dpDisplaySociety ? resolveLogoUrl(dpDisplaySociety) : null}
        availableWarranties={availableWarranties}
        currentStep={currentStep}
        breakpoint={breakpoint}
      />

      {/* Introduction du tutoriel (si activé dans le profil) */}
      {tutorialState.showIntro && isTutorialEnabled && (
        <TutorialIntro
          onStart={() => startTutorial(firstStepIndexForCurrentWizard !== -1 ? firstStepIndexForCurrentWizard : 0)}
          onSkip={() => { disableTutorials(); skipTutorial(); }}
        />
      )}

      {/* Tutoriel interactif (si activé dans le profil) */}
      {tutorialState.isActive && currentTutorialStep && isTutorialEnabled && (
        <TutorialSpotlight
          currentStep={currentTutorialStep as any}
          stepNumber={tutorialState.currentStepIndex + 1}
          totalSteps={tutorialSteps.length}
          isFieldValid={isTutorialFieldValid}
          onNext={handleNextTutorialStep}
          onPrevious={handlePreviousTutorialStep}
          showNavigation
          onSkip={() => { disableTutorials(); skipTutorial(); }}
          placement={currentTutorialStep.placement}
        />
      )}
    </>
  );
};
